// Copyright 2025. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import {
    intMinRule,
    ipRule,
    requiredRule,
    validate,
} from 'common/validator';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    Loader,
    Tooltip,
} from 'common/components';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import * as computeResourceActions from 'admin/computeResource/actions';
import {
    CodeEditor,
    Column,
    Columns,
    Form,
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
    FormFieldText,
    ISegmentedControlButton,
    Section,
    SelectOption,
    setIn,
    Translate,
} from '@plesk/ui-library';
import { BALANCE_STRATEGY } from 'common/api/resources/Storage';
import { nestStringProperties } from 'common/modules/app/formErrors/selectors';
import { Button } from 'admin/common/components/Button/Button';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Limit } from 'common/components/Limit/Limit';
import { dataCySelector } from 'common/tests/selectors';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectOption } from 'admin/user/components/Filters';
import {
    IComputeResourceResponse,
    IComputeResourceSettingNetwork,
    IComputeResourceSettingsNetworkRules,
    NetworkType,
    VIRTUALIZATION_TYPE_TRANSLATION_MAP,
    VirtualizationType,
} from 'common/api/resources/ComputeResource';
import { SegmentedControl } from 'common/components/SegmentedControl/SegmentedControl';
import { GiB } from 'common/helpers/units';
import { DiskCacheMode } from 'common/api/resources/ComputeResourceVm';
import { COMPUTE_RESOURCE_SETTINGS } from 'admin/computeResource/constants/tests';
import { ILimit } from 'common/api/resources/model';
import { humanizeNetworkType } from 'admin/computeResource/constants';
import { FormFieldNumber } from 'common/components/Form/FormFieldNumber/FormFieldNumber';
import { IConcurrentBackups } from 'common/api/resources/BackupNode';
import {
    Tab,
    TabList,
    TabPanel,
    Tabs,
} from 'react-tabs';
import { StyledCodeEditor } from 'admin/common/styles/CodeEditor';
import { initialState } from 'common/modules/settings/reducer';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';

interface IComputeResourceSettingsFormProps {
    computeResource: IComputeResourceResponse;
    onClose: () => void;
}

export type ComputeResourceSettingsFormProps =
    IComputeResourceSettingsFormProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

interface ISettings {
    iso_path: string;
    cache_path: string;
    backup_tmp_path: string;
    vnc_proxy_port: number;
    balance_strategy: string;
    network: IComputeResourceSettingNetwork;
    network_rules?: IComputeResourceSettingsNetworkRules;
    virtualization_types: VirtualizationType[];
    vm: ILimit;
    hdd: ILimit;
    ram: ILimit;
    vcpu: ILimit;
    vs_disk_cache_mode: DiskCacheMode | null;
    concurrent_backups: IConcurrentBackups;
    libvirt_xml_cpu: string | null;
    use_custom_libvirt: boolean;
}

const balanceStrategyOptions: ISelectOption[] = [
    {
        label: BALANCE_STRATEGY.RANDOM,
        value: BALANCE_STRATEGY.RANDOM,
    },
    {
        label: BALANCE_STRATEGY.ROUND_ROBIN,
        value: BALANCE_STRATEGY.ROUND_ROBIN,
    },
    {
        label: BALANCE_STRATEGY.MOST_STORAGE_AVAILABLE,
        value: BALANCE_STRATEGY.MOST_STORAGE_AVAILABLE,
    },
];

export const limitSections = [
    { key: 'vm', measure: 'units' },
    { key: 'hdd', measure: 'gib' },
    { key: 'ram', measure: 'gib' },
    { key: 'vcpu', measure: 'units' },
];

const diskCacheModeOptions = Object.values(DiskCacheMode).map((value) => (
    <option key={value} value={value}>
        {value}
    </option>
));

export const SettingsDialog: React.FC<ComputeResourceSettingsFormProps> = ({
    onClose,
    computeResource,
    formErrors,
    isItemCreating,
    isLoading,
    formErrorsActions: { setFormErrors, clearFormErrors },
    computeResourceActions: { updateSettings },
}) => {
    const [submitValues, setSubmitValues] = React.useState({
        ...computeResource.settings,
        vm: {
            limit: computeResource.settings.limits.vm.total,
            is_enabled: !computeResource.settings.limits.vm.unlimited,
        },
        hdd: {
            limit: computeResource.settings.limits.hdd.total,
            is_enabled: !computeResource.settings.limits.hdd.unlimited,
        },
        ram: {
            limit: Math.round(computeResource.settings.limits.ram.total / GiB),
            is_enabled: !computeResource.settings.limits.ram.unlimited,
        },
        vcpu: {
            limit: computeResource.settings.limits.vcpu.total,
            is_enabled: !computeResource.settings.limits.vcpu.unlimited,
        },
    });

    React.useEffect(() =>
        () => {
            clearFormErrors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    , []);

    const handleCreate = async (values: ISettings) => {
        const rules = {
            iso_path: requiredRule(<Translate content="validate.fieldRequired" />),
            cache_path: requiredRule(<Translate content="validate.fieldRequired" />),
            backup_tmp_path: requiredRule(<Translate content="validate.fieldRequired" />),
            vnc_proxy_port: requiredRule(<Translate content="validate.fieldRequired" />),
            vm: requiredRule(<Translate content="validate.fieldRequired" />),
            hdd: requiredRule(<Translate content="validate.fieldRequired" />),
            ram: requiredRule(<Translate content="validate.fieldRequired" />),
            vcpu: requiredRule(<Translate content="validate.fieldRequired" />),
            balance_strategy: requiredRule(<Translate content="validate.fieldRequired" />),
            virtualization_types: requiredRule(<Translate content="validate.fieldRequired" />),
            'concurrent_backups.create': intMinRule(<Translate content="validate.fieldRequired" params={{ min: 1 }}/>, 1),
            'concurrent_backups.restore': intMinRule(<Translate content="validate.fieldRequired" params={{ min: 1 }}/>, 1),
        };

        if (values.network.ip_for_vpc_network) {
            rules['network.ip_for_vpc_network'] = ipRule(<Translate content="validate.badIpAddress" />, 4);
        }

        if (values.use_custom_libvirt) {
            rules['libvirt_xml_cpu'] = requiredRule(<Translate content="validate.fieldRequired" />);
        }

        const errors = validate<ISettings>(values, rules);

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        clearFormErrors();

        await updateSettings(computeResource.id, {
            iso_path: values.iso_path,
            cache_path: values.cache_path,
            backup_tmp_path: values.backup_tmp_path,
            vnc_proxy_port: values.vnc_proxy_port,
            network: {
                type: values.network.type,
                bridges: values.network.bridges,
                ip_for_vpc_network: values.network.ip_for_vpc_network,
            },
            network_rules: values.network_rules,
            limits: {
                vm: { total: values.vm.limit, unlimited: !values.vm.is_enabled },
                hdd: { total: values.hdd.limit, unlimited: !values.hdd.is_enabled },
                ram: { total: values.ram.limit * GiB, unlimited: !values.ram.is_enabled },
                vcpu: { total: values.vcpu.limit, unlimited: !values.vcpu.is_enabled },
            },
            balance_strategy: values.balance_strategy,
            virtualization_types: values.virtualization_types,
            vs_disk_cache_mode: values.vs_disk_cache_mode || null,
            concurrent_backups: {
                create: values.concurrent_backups.create,
                restore: values.concurrent_backups.restore,
            },
            libvirt_xml_cpu: values.libvirt_xml_cpu,
            use_custom_libvirt: values.use_custom_libvirt,
        });
        onClose();
    };

    const handleFieldChange = (field: string, value: string) => setSubmitValues(setIn(submitValues, field, value));

    const createLimitOnChange = (key: string) => () => {
        setSubmitValues((state) => ({
            ...state,
            [key]: {
                ...state[key],
                is_enabled: !state[key].is_enabled,
                limit: !state[key].is_enabled ? state[key].limit : 0,
            },
        }));
    };

    const renderLimitField = (key: string, measure: string) => (
        <Limit
            key={key}
            onChangeIsEnabled={createLimitOnChange(key)}
            limit={submitValues[key]}
            fieldName={`${key}[limit]`}
            fieldLabel={<Translate content={`computeResource.settings.form.limits.${measure}`}/>}
            label={<Translate content={`computeResource.settings.form.limits.${key}`}/>}
            required={true}
            data-cy={dataCySelector(computeResource.id, key)}
        />
    );

    const getBalanceStrategyValue = (value: string) => balanceStrategyOptions.find((option) => option.value === value);

    const canChangeNetworkType = (from: NetworkType, to: NetworkType) => {
        switch(from) {
        case NetworkType.BRIDGED:
        case NetworkType.ROUTED:
            return [NetworkType.BRIDGED, NetworkType.ROUTED].includes(to);
        case NetworkType.VZ_ROUTED:
            return to === NetworkType.VZ_ROUTED;
        }
        return false;
    };

    const networkTypeOptions = Object.values(NetworkType).map((type) => {
        const canChange = canChangeNetworkType(submitValues.network.type, type);
        let tooltip;
        if (canChange || computeResource.vms_count > 0) {
            tooltip = null;
        } else {
            tooltip = type === NetworkType.VZ_ROUTED
                ? (<Translate content="computeResource.settings.form.network.vzRoutedIsNotAllowed"/>)
                : (<Translate content="computeResource.settings.form.network.vzRoutedIsOnlyAllowed"/>);
        }

        return {
            value: type.toString(),
            title: humanizeNetworkType(type),
            disabled: !canChange || computeResource.vms_count > 0,
            tooltip,
        };
    });

    const networkRulesItemOptions: Array<ISegmentedControlButton<boolean|null>> = [
        {
            value: null,
            title: <Translate content={'computeResource.settings.form.networkRules.options.global'} />,
        },
        {
            value: true,
            title: <Translate content={'computeResource.settings.form.networkRules.options.active'} />,
        },
        {
            value: false,
            title: <Translate content={'computeResource.settings.form.networkRules.options.inactive'} />,
        },
    ];

    const updateNetworkRule = (rule: keyof Required<IComputeResourceSettingsNetworkRules>, value: boolean|null) => {
        setSubmitValues((state) => ({
            ...state,
            network_rules: {
                ...state.network_rules,
                [rule]: value,
            },
        }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleBalanceStrategyChange = (option: any) => {
        setSubmitValues({
            ...submitValues,
            balance_strategy: option.value.toString(),
        });
    };

    const handleSetDefaultSettings = () => {
        if (submitValues.use_custom_libvirt) {
            setSubmitValues({
                ...submitValues,
                libvirt_xml_cpu: initialState.compute_resource.libvirt_xml_cpu,
            });
        }
    };

    return (
        <Loader isLoading={isLoading} center={false}>
            <Form
                id="settingsForm"
                onSubmit={handleCreate}
                onFieldChange={handleFieldChange}
                values={submitValues}
                errors={formErrors}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
                footerClassName="hidden"
            >
                <Section>
                    <FormFieldText
                        size="fill"
                        name="iso_path"
                        label={<Translate content="computeResource.settings.form.isoPath" />}
                        required={true}
                    />
                    <FormFieldText
                        size="fill"
                        name="cache_path"
                        label={<Translate content="computeResource.settings.form.cachePath" />}
                        required={true}
                    />
                    <FormFieldText
                        size="fill"
                        name="backup_tmp_path"
                        label={<Translate content="computeResource.settings.form.backupTmpPath" />}
                        required={true}
                    />
                    <FormField
                        label={<Translate content="computeResource.settings.form.concurrentBackups.title" />}
                        required={true}
                    >
                        <Columns gap={SIZE.MD} vertical={false}>
                            <Column width={219}>
                                <FormFieldNumber
                                    name="concurrent_backups[create]"
                                    label={<Translate content="computeResource.settings.form.concurrentBackups.create" />}
                                    size={SIZE.FILL}
                                    min={1}
                                    vertical
                                />
                            </Column>
                            <Column width={219}>
                                <FormFieldNumber
                                    name="concurrent_backups[restore]"
                                    label={<Translate content="computeResource.settings.form.concurrentBackups.restore" />}
                                    size={SIZE.FILL}
                                    min={1}
                                    vertical
                                />
                            </Column>
                        </Columns>
                    </FormField>
                    <FormFieldText
                        size="fill"
                        name="vnc_proxy_port"
                        label={<Translate content="computeResource.settings.form.vncProxyPort" />}
                        required={true}
                    />
                    <FormField
                        name="balance_strategy"
                        label={<Translate content="computeResource.settings.form.balanceStrategy" />}
                        required={true}
                        description={<Translate content="computeResource.settings.form.balanceStrategyDescription" />}
                    >
                        {({ getId }) => (
                            <SelectInput<ISelectOption>
                                inputId={getId()}
                                options={balanceStrategyOptions}
                                isClearable={false}
                                onChange={handleBalanceStrategyChange}
                                value={getBalanceStrategyValue(submitValues.balance_strategy)}
                            />
                        )}
                    </FormField>
                    <FormFieldSelect
                        required={true}
                        name="virtualization_types"
                        label={<Translate content="computeResource.settings.form.virtualizationTypes.title" />}
                        multiple
                        size={SIZE.FILL}
                        value={submitValues.virtualization_types}
                    >
                        {(computeResource.capabilities.kvm
                            || computeResource.settings.virtualization_types.includes(VirtualizationType.KVM)) && (
                            <SelectOption value={VirtualizationType.KVM}>
                                {VIRTUALIZATION_TYPE_TRANSLATION_MAP[VirtualizationType.KVM]}
                            </SelectOption>
                        )}
                        {(computeResource.capabilities.vz
                            || computeResource.settings.virtualization_types.includes(VirtualizationType.VZ)) && (
                            <SelectOption value={VirtualizationType.VZ}>
                                {VIRTUALIZATION_TYPE_TRANSLATION_MAP[VirtualizationType.VZ]}
                            </SelectOption>
                        )}
                    </FormFieldSelect>
                </Section>
                <Section vertical={true} title={<Translate content="computeResource.settings.form.network.title" />}>
                    <FormField
                        fullDescription={
                            <Translate
                                content="computeResource.settings.form.network.fullDescription"
                                params={{
                                    link: (
                                        <a
                                            href="https://docs.solusvm.com/v2/administrator-guide/Choosing%2Ba%2BCompute%2BResource%2BNetwork%2BType.html"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <Translate content="computeResource.settings.form.network.fullDescriptionLink" />
                                        </a>
                                    ),
                                }}
                            />
                        }
                        name="network[type]"
                        required={true}
                        label={<Translate content="computeResource.settings.form.network.type" />}
                    >
                        {({ setValue }) => (
                            <Tooltip
                                shown={computeResource.vms_count > 0}
                                title={<Translate content="computeResource.settings.form.network.description"/>}>
                                <SegmentedControl
                                    buttons={networkTypeOptions}
                                    selected={submitValues.network.type}
                                    onChange={setValue}
                                />
                            </Tooltip>
                        )}
                    </FormField>
                    <FormFieldText
                        size={SIZE.FILL}
                        name="network[ip_for_vpc_network]"
                        label={<Translate content="computeResource.settings.form.network.ipForVpcNetwork" />}
                    />
                </Section>
                <Section
                    vertical={true}
                    title={<Translate content="computeResource.settings.form.networkRules.title" />}
                >
                    <p className={'pul-hint'}>
                        <Translate
                            content="computeResource.settings.form.networkRules.description"
                            params={{
                                linkToNetworkRulesSettings: (
                                    <Link
                                        to={pathTo('settings/network_rules')}
                                        target="_blank"
                                    >
                                        <Translate content="computeResource.settings.form.networkRules.descriptionLink" />
                                    </Link>
                                ),
                            }}
                        />
                    </p>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.arp" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.arp ?? null}
                            onChange={(value) => updateNetworkRule('arp', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.dhcp" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.dhcp ?? null}
                            onChange={(value) => updateNetworkRule('dhcp', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.cloudInit" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.cloud_init ?? null}
                            onChange={(value) => updateNetworkRule('cloud_init', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.smtp" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.smtp ?? null}
                            onChange={(value) => updateNetworkRule('smtp', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.icmp" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.icmp ?? null}
                            onChange={(value) => updateNetworkRule('icmp', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.icmpReply" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.icmp_reply ?? null}
                            onChange={(value) => updateNetworkRule('icmp_reply', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.ipSpoofing" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.ip_spoofing ?? null}
                            onChange={(value) => updateNetworkRule('ip_spoofing', value)}
                        />
                    </FormField>
                    <FormField
                        label={<Translate content="computeResource.settings.form.networkRules.portmapper" />}
                    >
                        <SegmentedControl<boolean|null>
                            buttons={networkRulesItemOptions}
                            selected={submitValues.network_rules?.portmapper ?? null}
                            onChange={(value) => updateNetworkRule('portmapper', value)}
                        />
                    </FormField>
                </Section>
                <Section vertical={true} title={<Translate content="computeResource.settings.form.limits.title" />}>
                    {limitSections.map(item => renderLimitField(item.key, item.measure))}
                </Section>
                <Section title={<Translate content="computeResource.settings.form.kvm.title" />}>
                    <FormFieldSelect
                        size="fill"
                        name="vs_disk_cache_mode"
                        data-cy={COMPUTE_RESOURCE_SETTINGS.VS_DISK_CACHE_MODE}
                        description={<Translate content="computeResource.settings.form.kvm.diskCacheModeDescription" />}
                        clearable={true}
                        label={<Translate content="computeResource.settings.form.kvm.diskCacheMode" />}
                    >
                        {diskCacheModeOptions}
                    </FormFieldSelect>
                </Section>
                <Section title={<Translate content="computeResource.settings.form.libvirtXmlConfiguration" />}>
                    <FormFieldCheckbox
                        name="use_custom_libvirt"
                        label={
                            <Translate content="computeResource.settings.form.useCustomLibvirtConfiguration" />
                        }
                    />
                    <Tabs>
                        <div>
                            <TabList style={{ marginBottom: '0' }}>
                                <Tab>
                                    <Translate content="settings.computeResource.kvm.libvirtXmlConfigurationCpu"/>
                                </Tab>
                            </TabList>
                            <div></div>
                        </div>
                        <TabPanel>
                            <FormField
                                size={SIZE.MD}
                                name="libvirt_xml_cpu"
                            >
                                <StyledCodeEditor height={400} disabled={!submitValues.use_custom_libvirt}>
                                    <CodeEditor
                                        mode="xml"
                                        readOnly={!submitValues.use_custom_libvirt ? 'nocursor' : false}
                                        onChange={(value: string) => setSubmitValues({
                                            ...submitValues,
                                            libvirt_xml_cpu: value,
                                        })}
                                    >
                                        {submitValues.libvirt_xml_cpu ?? ''}
                                    </CodeEditor>
                                </StyledCodeEditor>
                            </FormField>
                            <Button
                                ghost={false}
                                onClick={handleSetDefaultSettings}
                            >
                                <Translate content="settings.computeResource.kvm.setDefaultLibvirtXml" />
                            </Button>
                        </TabPanel>
                    </Tabs>
                </Section>
            </Form>
            <Button
                type="submit"
                form="settingsForm"
                fill={true}
                intent={INTENT_TYPE.PRIMARY}
                size="lg"
                isLoading={isItemCreating}
            >
                <Translate content="computeResource.settings.form.saveBtn" />
            </Button>
        </Loader>
    );
};

const mapStateToProps = (state: RootState) => ({
    formErrors: nestStringProperties(state),
    isItemCreating: state.app.loadingFlags.has(LOADING_FLAGS.SAVE_COMPUTE_RESOURCE_SETTINGS),
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_SETTINGS),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsDialog);
